import React, { useState, useEffect } from 'react';
import { IconButton } from '@material-ui/core';
import { Create as EditIcon, Delete as DeleteIcon } from '@material-ui/icons';
import AutoCompleteAdministratorName from '../../components/AutoComplete/AutoCompleteAdministratorName';
import AutoCompleteAccountNo from '../../components/AutoComplete/AutoCompleteAccountNo';
import SelectPage from '../../components/AutoComplete/SelectPage';
import Table, {
  columnType,
  tempFilteredRows,
} from '../../components/ClearingTable/Table';
import SelectRole from '../../components/Dropdown/SelectRole';
import Button from '../../components/Buttons/Button';
import Text from '../../components/Typography/Text';
import {
  notifySuccess,
  notifyInfo,
  notifyError,
} from '../../components/Messages/Notification';
import { listRole, createRole, updateRole, deleteRole } from '../../services/RoleService';
import { protoTimeSpanObjectToString } from '../../services/ConvertService';
import RoleModal from './Components/RoleModal';
import { useConfirm } from 'material-ui-confirm';
import { getCurrentUser } from '../../services/AuthService';
import { formatPbDateTimeWithMillis } from '../../lib/fmt';

export default function RoleManager() {
  const [rowData, setRowData] = useState({});
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchData, setSearchData] = useState({
    roleId: 0,
    roleName: '',
    usrId: 0,
    accountNo: '',
    accountId: 0,
    pageId: '',
    pageName: '',
    name: '',
  });
  const confirm = useConfirm();
  const [pagination, setPagination] = useState({});
  const { IsAdmin } = getCurrentUser();

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    setLoading(true);

    try {
      let { rolesList } = await listRole(searchData);

      setRows(rolesList);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const columns = [
    {
      name: 'actions',
      label: 'Actions',
      type: columnType.buttons,
      options: {
        customBodyRenderLite: (dataIndex) => {
          if (!IsAdmin) {
            return <div style={{ margin: 0, padding: 0 }}></div>;
          }
          return (
            <div
              align={'left'}
              className="grd-row"
              style={{ margin: 0, paddingLeft: 10 }}
            >
              <IconButton
                aria-label="edit"
                disabled={loading}
                onClick={() => {
                  handleOpen(tempFilteredRows[dataIndex]);
                }}
              >
                <EditIcon style={{ height: 20, width: 20 }} />
              </IconButton>
              {tempFilteredRows[dataIndex]?.userCount === '' && (
                <IconButton
                  aria-label="delete"
                  disabled={loading}
                  onClick={() => {
                    handleDelete(tempFilteredRows[dataIndex]);
                  }}
                >
                  <DeleteIcon style={{ height: 20, width: 20 }} />
                </IconButton>
              )}
            </div>
          );
        },
        sort: false,
      },
    },
    {
      name: 'accountCount',
      label: 'Account',
    },
    {
      name: 'pageCount',
      label: 'Access',
    },
    {
      name: 'roleName',
      label: 'Role Name',
    },
    {
      name: 'userCount',
      label: 'User',
    },
    {
      name: 'createdBy',
      label: 'Created By',
      options: {
        display: false,
      },
    },
    {
      name: 'createdAt',
      label: 'Created At',
      options: {
        display: false,
        customBodyRenderLite: (dataIndex) => {
          return protoTimeSpanObjectToString(
            rows[dataIndex]?.createdAt,
            'MM/DD/YYYY hh:mm:ss a'
          );
        },
      },
    },
    {
      name: 'modifiedAt',
      label: 'Modified At',
      options: {
        display: false,
        customBodyRenderLite: (dataIndex) => {
          return protoTimeSpanObjectToString(
            rows[dataIndex]?.modifiedAt,
            'MM/DD/YYYY hh:mm:ss a'
          );
        },
      },
    },
    {
      name: 'modifiedBy',
      label: 'Modified By',
      options: {
        display: false,
      },
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    serverSide: true,
    selectableRows: 'none',
    responsive: 'standard',
  };

  const handleSearch = async (paginationChange) => {
    try {
      setLoading(true);
      let paginationCopy = {
        ...pagination,
        reload: false,
        pageNo: paginationChange === true ? pagination.pageNo : 0,
        rowsPerPage: pagination.rowsPerPage || 100,
      };

      if (paginationChange !== true) {
        paginationCopy.filterNames = [];
        paginationCopy.filterValues = [];
      }

      const { rolesList } = await listRole(searchData, pagination);
      setRows(rolesList);
      if (paginationChange !== true) {
        notifyInfo(rolesList.length + ' search results.');
      }
    } catch (error) {
      notifyError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (data) => {
    if (!data) {
      //ADD default values
      data = {
        roleName: '',
      };
    }

    setRowData(data);
    setOpen(true);
  };

  const handleClose = async (data, isEdit) => {
    if (!data) {
      setOpen(false);
      handleSearch(true);
      return;
    }

    let valid = true;

    if (!data.roleName) {
      notifyError('Role name is required.');
      valid = false;
    }

    if (!valid) {
      return;
    }
    data.defaultAccess = 'None';
    setLoading(true);
    let resp;
    try {
      if (isEdit) {
        //EDIT
        resp = await updateRole(data);
        const rowsCopy = [...rows];
        const index = rows.indexOf(rowData);
        rowsCopy[index] = resp.roleList;
        setRows(rowsCopy);
        notifySuccess('Role has been updated.');
      } else {
        //ADD
        resp = await createRole(data);
        setRows([resp.roleList, ...rows]);
        notifySuccess('New Role has been added.');
      }

      setOpen(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
      notifyError(error.message);
    } finally {
      if (!isEdit) {
        handleOpen(resp);
      }
    }
    handleSearch(true);
    setLoading(false);
  };

  const handleDelete = async (data) => {
    try {
      confirm({
        description:
          "You are about to delete '" + data.roleName + "'. Please confirm your action.",
        confirmationText: 'Yes, delete',
      }).then(async () => {
        setLoading(true);
        await deleteRole(data);
        notifySuccess('Role has been successfully deleted');
        handleSearch(true);
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getCsvData = async () => {
    if (rows.length === 0) {
      return [];
    }
    const csvData = rows.map((data) => {
      data.modifiedAt = formatPbDateTimeWithMillis(data.modifiedAt);

      return data;
    });

    return csvData;
  };

  return (
    <div className="page-c">
      <div className="search-c">
        <div className="grd-row nm">
          <div className="grd-cell">
            <Text mt={8} variant="h1" label="Role Manager" />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading.search}
              loading={loading.search}
              type="search"
              label={loading.search ? 'Searching...' : 'Search'}
              onClick={handleSearch}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell" style={{ marginRight: 30 }}>
            <SelectRole
              name="roleName"
              label="Role Name"
              value={searchData.roleName}
              onChange={({ currentTarget: { roleId, value } }) => {
                setSearchData({
                  ...searchData,
                  roleName: roleId ? value : '',
                  roleId: roleId,
                });
              }}
            />
          </div>
          <div className="grd-cell" style={{ marginRight: 30 }}>
            <AutoCompleteAdministratorName
              name="name"
              label="User"
              value={searchData.name}
              onChange={({
                currentTarget: {
                  details: { name, userId: usrId },
                },
              }) => {
                setSearchData({ ...searchData, name, usrId });
              }}
            />
          </div>
          <div className="grd-cell" style={{ marginRight: 30 }}>
            <AutoCompleteAccountNo
              name="accountNo"
              label="Account No"
              value={searchData.accountNo}
              onChange={({ currentTarget: { data } }) => {
                setSearchData({
                  ...searchData,
                  accountNo: data.accountNo || '',
                  accountId: data.accountId || 0,
                });
              }}
              type="client"
              isAccessibleOnly={true}
            />
          </div>
          <div className="grd-cell" style={{ marginRight: 30 }}>
            <SelectPage
              name="pageName"
              label="Page Name"
              value={searchData.pageName || ''}
              onChange={({ currentTarget: { pageId, value } }) => {
                setSearchData({
                  ...searchData,
                  pageName: value,
                  pageId: pageId,
                });
              }}
            ></SelectPage>
          </div>
        </div>
      </div>
      <div className="mt-20">
        <Table
          title={'Role Manager'}
          data={rows}
          columns={columns}
          options={options}
          pagination={pagination}
          setPagination={setPagination}
          onSearch={handleSearch}
          getCsvData={getCsvData}
        />
      </div>
      {open && <RoleModal onClose={handleClose} open={open} value={rowData}></RoleModal>}
    </div>
  );
}
